import React from 'react';
import type { UserDetailsResponse } from '../../schemas/validation';
import { formatFileSize } from './utils';

interface SidebarCardsProps {
  media: UserDetailsResponse['data']['media'];
  components: UserDetailsResponse['data']['components'];
  pageTemplates: UserDetailsResponse['data']['pageTemplates'];
  siteTemplates: UserDetailsResponse['data']['siteTemplates'];
  insights: UserDetailsResponse['data']['insights'];
}

export const SidebarCards: React.FC<SidebarCardsProps> = ({
  media,
  components,
  pageTemplates,
  siteTemplates,
  insights,
}) => {
  return (
    <div className="space-y-8">
      {/* Media Usage */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Media Usage</h3>
        </div>
        <div className="px-6 py-4">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Files</dt>
              <dd className="mt-1 text-sm text-gray-900">{media.totalFiles}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Size</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatFileSize(media.totalSizeBytes)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">File Types</dt>
              <dd className="mt-1">
                <div className="flex flex-wrap gap-1">
                  {media.fileTypes.map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">By Asset Type</dt>
              <dd className="mt-1">
                <div className="space-y-2">
                  {Object.entries(media.byAssetType).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="text-sm text-gray-600 capitalize">{type}</span>
                      <span className="text-sm text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Components */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Components</h3>
        </div>
        <div className="px-6 py-4">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Components</dt>
              <dd className="mt-1 text-sm text-gray-900">{components.totalComponents}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Public Components</dt>
              <dd className="mt-1 text-sm text-gray-900">{components.publicComponents}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Private Components</dt>
              <dd className="mt-1 text-sm text-gray-900">{components.privateComponents}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">By Type</dt>
              <dd className="mt-1">
                <div className="space-y-2">
                  {Object.entries(components.byType).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="text-sm text-gray-600 capitalize">{type}</span>
                      <span className="text-sm text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Templates */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Templates</h3>
        </div>
        <div className="px-6 py-4">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Page Templates</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {pageTemplates.totalTemplates} ({pageTemplates.publicTemplates} public)
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Site Templates</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {siteTemplates.totalTemplates} ({siteTemplates.publicTemplates} public)
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Template Contributor</dt>
              <dd className="mt-1">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${insights.templateContributor ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                >
                  {insights.templateContributor ? 'Yes' : 'No'}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Insights</h3>
        </div>
        <div className="px-6 py-4">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Avg Pages per Site</dt>
              <dd className="mt-1 text-sm text-gray-900">{insights.avgPagesPerSite.toFixed(1)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Most Active Month</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {insights.mostActiveMonth || 'Not enough data'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Preferred Content Types</dt>
              <dd className="mt-1">
                <div className="flex flex-wrap gap-1">
                  {insights.preferredContentTypes.map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};
